import React from 'react';
import { Link } from 'react-router-dom';
import ApexGlyph from './ApexGlyph';
import { openCookiePreferences } from '@/components/CookieConsent';

/**
 * LandingFooter
 * -------------
 * Unified footer for all public landing pages.
 *
 * Composition (centered, restrained, infrastructural):
 *
 *   ────────────────────────────────────────────────
 *     [glyph] APEX VERITY                              ← brand row (left)
 *     Governance Proof Infrastructure
 *
 *     Why Apex · Platform · Lens · Trust ...           ← nav row (right on desktop)
 *
 *   ────────────────────────────────────────────────
 *     © 2026 APEX Verity      ·    apexverity.io       ← legal row
 *
 * Background continues the deep graphite system. Top hairline separator
 * (#14171F) maintains continuity with the rest of the design language.
 */

const NAV_COLS = [
  {
    label: 'Platform',
    items: [
      ['Why Apex',     '/why-apex'],
      ['Platform',     '/platform'],
      ['How it works', '/lens/how-it-works'],
    ],
  },
  {
    label: 'Products',
    items: [
      ['Apex Lens',  '/product/lens'],
      ['Apex Trust', '/product/trust'],
    ],
  },
  {
    label: 'Engage',
    items: [
      ['Request pilot', '/contact'],
      ['Contact',       '/contact'],
    ],
  },
];

const colHeader = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 10,
  color: '#475569',
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  marginBottom: 16,
};
const colLink = {
  display: 'block',
  fontSize: 13,
  color: '#94A3B8',
  textDecoration: 'none',
  letterSpacing: '0.01em',
  padding: '5px 0',
  transition: 'color 0.2s ease',
};

export default function LandingFooter() {
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="landing-footer"
      style={{
        background: '#06070A',
        borderTop: '1px solid #14171F',
        marginTop: 80,
        color: '#94A3B8',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '72px 24px 32px',
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 1.4fr) repeat(3, minmax(140px, 1fr))',
          gap: 48,
        }}
      >
        {/* Brand block */}
        <div>
          <Link
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
            data-testid="footer-brand"
          >
            <ApexGlyph size={28} color="#E2E8F0" />
            <span
              style={{
                color: '#E2E8F0',
                fontWeight: 500,
                fontSize: 13,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
              }}
            >
              APEX&nbsp;Verity
            </span>
          </Link>
          <p
            style={{
              marginTop: 20,
              fontSize: 12,
              color: '#475569',
              lineHeight: 1.7,
              letterSpacing: '0.04em',
              maxWidth: 280,
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            Temporal governance proof infrastructure for cloud and AI systems.
          </p>
        </div>

        {/* Nav columns */}
        {NAV_COLS.map((col) => (
          <div key={col.label}>
            <div style={colHeader}>{col.label}</div>
            {col.items.map(([label, to]) => (
              <Link
                key={`${col.label}-${label}-${to}`}
                to={to}
                style={colLink}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#F1F5F9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
                data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {label}
              </Link>
            ))}
          </div>
        ))}
      </div>

      {/* Legal row */}
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '24px 24px 32px',
          borderTop: '1px solid #14171F',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            color: '#475569',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
          data-testid="footer-copyright"
        >
          © {year} APEX Verity
        </span>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <Link
            to="/privacy"
            data-testid="footer-privacy-link"
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11,
              color: '#475569',
              letterSpacing: '0.18em',
              textDecoration: 'none',
              textTransform: 'uppercase',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; }}
          >
            Privacy
          </Link>
          <button
            type="button"
            onClick={openCookiePreferences}
            data-testid="footer-cookie-prefs-btn"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11,
              color: '#475569',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; }}
          >
            Cookie Preferences
          </button>
          <span
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11,
              color: '#475569',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            Managed like SaaS
          </span>
        </div>
        <a
          href="https://apexverity.io"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            color: '#475569',
            letterSpacing: '0.18em',
            textDecoration: 'none',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; }}
          data-testid="footer-domain"
        >
          apexverity.io →
        </a>
      </div>

      {/* Responsive: collapse grid on narrow viewports */}
      <style>{`
        @media (max-width: 720px) {
          footer[data-testid="landing-footer"] > div:first-child {
            grid-template-columns: 1fr 1fr !important;
            gap: 32px !important;
            padding: 56px 24px 24px !important;
          }
          footer[data-testid="landing-footer"] > div:first-child > div:first-child {
            grid-column: span 2;
          }
        }
        @media (max-width: 480px) {
          footer[data-testid="landing-footer"] > div:first-child {
            grid-template-columns: 1fr !important;
          }
          footer[data-testid="landing-footer"] > div:first-child > div:first-child {
            grid-column: span 1;
          }
        }
      `}</style>
    </footer>
  );
}
